import { html, LitElement } from 'lit';
import { nothing } from 'lit-html';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { classMap } from 'lit-html/directives/class-map.js';
import { style } from './iconic-vehicle-loan-card.style.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import '../../iconic-vehicle/iconic-vehicle-card/iconic-vehicle-card.component.js';
import '../../iconic-loan/iconic-loan-card/iconic-loan-card.component.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../../shared/constants/app.constants.js';
import { tags } from '../../../shared/constants/tags.js';
import { stratum } from '../../../../constants/stratum.js';
import { ApplicationService } from '../../../services/application.service.js';

class RefiIconicVehicleLoanCard extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicVehicleLoanCard');
    this.vehicleAndLoanIndex = -1;
    this.vehicleAndLoanId = '';
    this.showPlaceholder = false;
    this.showButton = true;
    this.canEdit = false;
    this.multiple = false;
    this.hideIneligibility = false;
    this.tagBalance = false;
  }

  static properties = {
    vehicle: { type: Object, attribute: true },
    loan: { type: Object, attribute: true },
    vehicleAndLoanIndex: { type: Number },
    vehicleAndLoanId: { type: String },
    showPlaceholder: { type: Boolean },
    showButton: { type: Boolean },
    canEdit: { type: Boolean },
    multiple: { type: Boolean },
    hideIneligibility: { type: Boolean, attribute: 'hide-ineligibility' },
    tagBalance: { type: Boolean },
  };

  static styles = [style];

  connectedCallback() {
    super.connectedCallback();
    this.vehicleAndLoanId = `vehicleAndLoanButton${this.vehicleAndLoanIndex}`;
  }

  get isVehicleIneligible() {
    let isIneligible = false;
    if (this.vehicle.ineligibleReasons == null) {
      isIneligible = false;
    } else {
      isIneligible = this.vehicle.ineligibleReasons.length > 0;
    }
    return isIneligible;
  }

  get isLoanIneligible() {
    let isIneligible = false;
    if (this.loan?.ineligibleReasons == null) {
      isIneligible = false;
    } else {
      isIneligible = this.loan?.ineligibleReasons.length > 0;
    }
    return isIneligible;
  }

  selectThisVehicleAndLoan() {
    this.dispatchEvent(
      new CustomEvent('selectVehicle', {
        detail: {
          vehicle: this.vehicle,
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('selectLoan', {
        detail: {
          loan: this.loan,
        },
      })
    );
    REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
      tags.vehicleAndLoan.selected(
        this.vehicle?.year,
        this.vehicle?.make,
        this.vehicle?.model,
        this.loan?.lenderName
      )
    );
    if (ApplicationService.isInAppDeclineDelayOn) {
      stratum.publishTag(
        TagKey.VEHICLE_LOAN_VEHICLE_LOAN_COMBINATION_SELECTED_2,
        {
          replacements: {
            value: `${this.vehicle?.year} ${this.vehicle?.make} ${this.vehicle?.model} - ${this.loan.lenderName}`,
          },
        }
      );
      stratum.publishTag(
        TagKey.ICONIC_VEHICLE_AND_LOAN_CHECK_ELIGIBILITY_CTA_2
      );
      stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_CALCULATED_LTV, {
        replacements: {
          value: `${
            (Number(this.loan.currentBalance) /
              Number(this.vehicle?.wholeSaleValue)) *
            100
          }%`,
        },
      });
    } else {
      stratum.publishTag(
        TagKey.VEHICLE_LOAN_VEHICLE_LOAN_COMBINATION_SELECTED,
        {
          replacements: {
            value: tags.vehicleAndLoan.selected(
              this.vehicle?.year,
              this.vehicle?.make,
              this.vehicle?.model,
              this.loan?.lenderName
            ),
          },
        }
      );
      stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_CHECK_ELIGIBILITY_CTA);
    }
    this.dispatchEvent(new CustomEvent('go-to-review'));
  }

  renderButton() {
    if (
      !this.hideIneligibility &&
      (this.isVehicleIneligible || this.isLoanIneligible)
    ) {
      return html``;
    }
    return html`
      <fs-button
        id="${this.vehicleAndLoanId}"
        name="${this.vehicleAndLoanId}"
        variant="primary"
        @click="${this.selectThisVehicleAndLoan}"
        class="card-button"
        delegated-aria-label="Check eligibility for ${this.vehicle.year} ${this
          .vehicle.make} ${this.vehicle.model}"
      >
        ${ApplicationService.isInvisiblePQ ? 'Check eligibility' : 'Looks good'}
      </fs-button>
    `;
  }

  render() {
    this.debug.log(this.multiple);
    return html`
      <fs-container data-e2e-ref="vehicle-loan-card">
        <div class="card-container">
          <div class="inner-container">
            <div class="grid ${classMap({ 'grid-multiple': !this.multiple })}">
              <refi-iconic-vehicle-card
                class="vehicle-card"
                .vehicle="${this.vehicle}"
                vehicleId="${this.vehicleAndLoanIndex}"
                inline
                inline-ineligible="${this.isVehicleIneligible ||
                this.isLoanIneligible
                  ? ''
                  : nothing}"
                .canEdit="${this.showPlaceholder}"
                hide-ineligibility="${this.hideIneligibility ? '' : nothing}"
              ></refi-iconic-vehicle-card>
              <refi-iconic-loan-card
                .loan="${this.loan}"
                loanId="${this.vehicleAndLoanIndex}"
                inline
                inline-ineligible="${this.isVehicleIneligible ||
                this.isLoanIneligible
                  ? ''
                  : nothing}"
                .showPlaceholder="${this.showPlaceholder}"
                hide-ineligibility="${this.hideIneligibility ? '' : nothing}"
                .tagBalance="${this.tagBalance}"
              ></refi-iconic-loan-card>
            </div>
            ${!this.showButton ? nothing : this.renderButton()}
          </div>
          ${!this.showPlaceholder && this.canEdit
            ? html` <fs-button
                variant="primary"
                type="button"
                icon-position="leading"
                delegated-aria-label="Edit this ${this.vehicle.year} ${this
                  .vehicle.make} ${this.vehicle.model}"
                ghost
                compact
                @click="${this.editVehicle}"
                ><svg
                  slot="button-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="m17.854 7.773-1.271 1.27-1.626-1.626 1.27-1.27a.502.502 0 0 1 .712-.001l.915.915a.505.505 0 0 1 0 .712ZM8.17 17.456l-2.033.407.407-2.033 7.706-7.705.813.813.812.813-7.705 7.705ZM18.56 6.354l-.914-.915a1.507 1.507 0 0 0-2.126 0l-1.624 1.625L5.73 15.23a.498.498 0 0 0-.137.255l-.583 2.917a.501.501 0 0 0 .588.588l2.917-.583a.505.505 0 0 0 .256-.137l8.166-8.166 1.625-1.624a1.508 1.508 0 0 0-.001-2.126Z"
                  ></path>
                </svg>
                Edit</fs-button
              >`
            : ''}
        </div>
      </fs-container>
    `;
  }

  editVehicle() {
    stratum.publishTag(
      ApplicationService.isInAppDeclineDelayOn
        ? TagKey.ICONIC_VEHICLE_AND_LOAN_EDIT_VEHICLE_LINK
        : TagKey.ICONIC_VEHICLE_AND_LOAN_EDIT_CAR_BUTTON
    );
    this.dispatchEvent(
      new CustomEvent('go-to-vehicle', { bubbles: true, composed: true })
    );
  }
}

customElements.define(
  'refi-iconic-vehicle-loan-card',
  RefiIconicVehicleLoanCard
);
